<template>
    <div class="documents">
        <div class="slider">
            <g-select v-model="doc"
                      v-model:index="docIndex"
                      class="type-select"
                      v-bind:options="documents"
                      option-text="name"
                      option-value="key"/>

            <div class="slide-container">
                <transition name="slide-fade" mode="out-in">
                    <div class="slide" v-bind:key="`doc-${docIndex}`">
                        <label v-if="!documents[docIndex].url" class="upload-btn">
                            <g-symbol name="import" width="16" height="16"/>
                            {{ $t('base.upload') }}
                            <b>{{ documents[docIndex].name }}</b>
                            <input type="file" accept="image/jpeg, image/png, image/webp" v-on:change="onFileChange($event, doc)" hidden/>
                        </label>
                        <template v-else>
                            <!--<img v-bind:src="`${host}${docs[doc]}`" v-bind:alt="doc"/>-->
                            <img v-bind:src="`${host}${documents[docIndex].url}`" v-bind:alt="documents[docIndex].name"/>
                            <span class="remove-btn" v-on:click.stop="removeDocument(docIndex)">✕</span>
                        </template>
                    </div>
                </transition>
            </div>
            <g-flex class="previews" gap="5" wrap="wrap" justify="center">
                <div v-for="(item, i) in documents"
                     v-bind:key="`preview-${item.key}`"
                     v-on:click="docIndex = i"
                     class="preview"
                     v-bind:class="{active: i === docIndex}">
                    <img v-if="item.url"
                         v-bind:src="`${host}${item.url}`"
                         v-bind:alt="item.name"/>
                    <template v-else>{{ item.name }}</template>
                </div>
            </g-flex>
        </div>
    </div>
</template>

<script>

import { onMounted, ref, computed } from 'vue';
import { STACK, ApiClient } from '@/Ship';
import { useProfileActions } from '@profile';

const getProfileResource = useProfileActions('getProfileResource');

const uploadDocument = useProfileActions('uploadDocument');

export default {
    name: 'Documents',

    setup() {
        const profile = getProfileResource();

        const doc = ref('id_front');
        const docIndex = ref(0);
        let documents = ref([
            {
                url: null,
                key: 'id_front',
                name: 'ID Card (front)'
            },
            {
                url: null,
                key: 'id_back',
                name: 'ID Card (back)'
            },
            {
                url: null,
                key: 'card_front',
                name: 'Card (front)'
            },
            {
                url: null,
                key: 'card_back',
                name: 'Card (back)'
            },
            {
                url: null,
                key: 'dod',
                name: 'DOD'
            },
            {
                url: null,
                key: 'ub',
                name: 'UB'
            },
        ]);

        onMounted(() => {
            for (const key in profile.documents) {
                const index = documents.value.findIndex(doc => doc.key === key);
                if (index > -1) {
                    documents.value[index].url = profile.documents[key].url;
                }
            }
        });

        const onFileChange = (e, target) => {
            let files = e.target.files || e.dataTransfer.files;
            return !files.length ? null : createImage(files[0], target);
        };
        const createImage = (file, target) => {
            let img = new Image();
            let reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = event => {
                img.src = event.target.result;

                img.onload = function () {

                    let canvas = document.createElement('canvas');
                    let ctx = canvas.getContext('2d');

                    canvas.width = (img.width > img.height)
                        ? 800
                        : 500;
                    canvas.height = img.height * (canvas.width / img.width);

                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);

                    url2File(canvas.toDataURL('image/jpeg'), target, 'image/jpeg').then((encodedFile) => {
                        uploadDocument(target, encodedFile).then((res) => {
                            documents.value[docIndex.value].url = res.url;
                        });
                    });
                };
            };
        };

        //return a promise that resolves with a File instance
        const url2File = (url, filename, mimeType) => {
            return (fetch(url)
                .then(function (res) {return res.arrayBuffer();})
                .then(function (buf) {return new File([buf], `${filename}.jpeg`, { type: mimeType });})
            );
        };

        const host = computed(() => {
            return process.env.VUE_APP_SERVER;
        });

        function removeDocument(index) {
            STACK.push(() => {
                return ApiClient.delete(`profile/document/${documents.value[index].key}`);
            }).then(() => {
                documents.value[index].url = null;
            });
        }

        return {
            host,
            doc,
            docIndex,
            documents,
            onFileChange,
            removeDocument,
        };
    }
};
</script>

<style lang="scss" scoped>
.type-select {
    display: inline-flex !important;
}

.slider {
    max-width: 800px;
    aspect-ratio: 15/10;
    margin: 0 auto;

    & > .slide-container {
        white-space: nowrap;
        overflow: auto;
        width: 100%;
        height: 100%;
        margin-top: 10px;
        border-radius: var(--bar-rounding, $bar-rounding);
        border: 1px solid var(--separator-color, $separator-color);
        background-color: var(--env-bg, $env-bg);

        & > .slide {
            width: 100%;
            height: 100%;
            position: relative;

            & > img {
                width: 100%;
                height: 100%;
                padding: 22px;
                object-fit: contain;
            }

            .remove-btn {
                cursor: pointer;
                font-size: 22px;
                top: 0;
                right: 5px;
                position: absolute;
                transition: transform 0.3s;
                color: white;
                text-shadow: 0 0 8px #000000aa;

                &:hover {
                    transform: scale(1.4);
                }
            }

            & > .upload-btn {
                transform: translate(-50%, -50%);
                position: absolute;
                top: 50%;
                left: 50%;
                padding: 10px;
                cursor: pointer;
                min-width: 208px;
                text-align: center;
                background-color: transparent;
                border: 1px solid var(--primary, $primary);
                border-radius: var(--btn-rounding, $btn-rounding);
                color: var(--main-text-color, $main-text-color);

                & > .g-symbol {
                    fill: var(--main-text-color, $main-text-color);
                }

                & > b {
                    text-transform: uppercase;
                }

                &:hover {
                    color: var(--title-text-color, $title-text-color);
                    background-color: var(--primary, $primary);

                    & > .g-symbol {
                        fill: var(--title-text-color, $title-text-color);
                    }
                }
            }
        }
    }
}

.previews {
    padding: 10px 0;
}

.preview {
    overflow: hidden;
    cursor: pointer;
    white-space: nowrap;
    text-align: center;
    height: 78px;
    line-height: 72px;
    aspect-ratio: 16/10;
    border-radius: 5px;
    border: 1px solid var(--separator-color, $separator-color);
    background-color: var(--env-bg, $env-bg);

    & > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &:hover {
        border-color: var(--primary-hover, $primary-hover);
    }

    &.active {
        border-color: var(--primary, $primary);
    }
}

.slide-fade-leave-active {
    transition: transform 0.4s cubic-bezier(1, 0.5, 0.8, 1), opacity 0.3s;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: scale(0.8);
    opacity: 0;
}
</style>
